/*
 * How the proctoring review writes a stretch of time. Ported from
 * unberry-4.0 `web/src/lib/duration.ts`; the clock reuses the dashboard's own
 * `getFormattedSeekValue`, so the room and the existing players agree.
 * Every time here is in seconds from the start of the round.
 */
import { getFormattedSeekValue } from "src/utils/helperFunctions";

/**
 * "04:32": minutes and seconds from the start of the round ("01:01:40" past
 * an hour). Rounds to the nearest second, as 4.0 does, then formats it with
 * the dashboard's seek-bar formatter (which floors).
 * @param {number} s seconds
 * @returns {string}
 */
export const clockOf = (s) => getFormattedSeekValue(Math.max(0, Math.round(Number(s) || 0)));

/**
 * A clock time back to seconds: "4:32", "04:32", "123:05" (m:ss, 1-3 digit
 * minutes) or "1:01:40" (h:mm:ss). Null when it is not a clock time.
 * @param {string|null|undefined} v
 * @returns {number|null}
 */
export function parseClock(v) {
  const text = (v || "").trim();
  const ms = /^(\d{1,3}):([0-5]\d)$/.exec(text);
  if (ms) return Number(ms[1]) * 60 + Number(ms[2]);
  const hms = /^(\d{1,2}):([0-5]\d):([0-5]\d)$/.exec(text);
  if (hms) return Number(hms[1]) * 3600 + Number(hms[2]) * 60 + Number(hms[3]);
  return null;
}

/* Whole minutes and seconds. 4.0 rounded the seconds after splitting, so
   119.6 came out as "1 min 60 s"; rounding first keeps it "2 min". */
const minSec = (s) => {
  const x = Math.max(0, Math.round(Number(s) || 0));
  return [Math.floor(x / 60), x % 60];
};

/**
 * "15m 37s", "41s", "36m": the report's compact form.
 * @param {number} s seconds
 * @returns {string}
 */
export function compact(s) {
  const [m, x] = minSec(s);
  if (!m) return `${x}s`;
  return x ? `${m}m ${x}s` : `${m}m`;
}

/**
 * "1 min 20 s", "45 s", "36 min": how long something lasted, in the review's words.
 * @param {number} s seconds
 * @returns {string}
 */
export function span(s) {
  const [m, x] = minSec(s);
  if (!m) return `${x} s`;
  return x ? `${m} min ${x} s` : `${m} min`;
}

/**
 * "2.9 s": a short stretch to the tenth of a second, rounded down, so a
 * sound just under a threshold never reads as the threshold itself.
 * @param {number} s seconds
 * @returns {string}
 */
export const tenths = (s) => `${Math.floor(Math.max(0, Number(s) || 0) * 10 + 1e-9) / 10} s`;
